import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIcon from '@mui/icons-material/DragIndicatorOutlined';
import MoreIcon from '@mui/icons-material/MoreHorizOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import sx from 'classnames';
import IconButton from 'components/IconButton/IconButton';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { FIELD_LABELS } from 'modules/connect-card/constants/field-types';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

function toBool(value) {
  if (value === '1') return true;
  if (value === '0') return false;
  return value;
}

const useStyles = makeStyles((theme) => ({
  root: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    marginRight: 4,
    width: 280,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid',
    borderColor: theme.palette.grey['400'],
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    minWidth: 0,
    flex: 1,
    transition: 'all .3s ease',
    '&.selected': {
      borderColor: theme.palette.primary.main,
    },
  },
  subtitle: {
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  description: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dragHandle: {
    cursor: 'move',
  },
}));

const Question = ({
  index,
  question,
  onRemoveQuestion,
  selected,
  onSelect,
  onConfigure,
}) => {
  const [t] = useTranslation('connectCard');
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: question.id });

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'questionActions',
  });

  const handleDelete = () => {
    popupState.close();
    onRemoveQuestion(question);
  };

  const handleConfigure = () => {
    popupState.close();
    onConfigure(question);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isRequiredQuestion =
    toBool(question.is_first_name) ||
    toBool(question.is_email_address) ||
    toBool(question.is_mobile);

  return (
    <Box
      index={index}
      {...attributes}
      style={style}
      ref={setNodeRef}
      display="flex"
      alignItems="center"
    >
      <Box
        key={question.id}
        className={classes.root}
        mb={1}
        onClick={() => onSelect(question)}
      >
        <Box className={sx(classes.container, { selected })}>
          <Box flex={1} overflow="hidden">
            <Typography variant="caption" className={classes.subtitle}>
              {index + 1}. {FIELD_LABELS[question.question_type]}
            </Typography>
            <Typography className={classes.description}>
              {question.question_label}
            </Typography>
          </Box>
          <Box>
            <IconButton
              size="small"
              edge="end"
              icon={<MoreIcon />}
              {...bindTrigger(popupState)}
            />
            <Menu {...bindMenu(popupState)}>
              <MenuItem onClick={handleConfigure}>
                {t('common:edit', 'Edit')}
              </MenuItem>
              {!isRequiredQuestion && (
                <>
                  <Divider />
                  <MenuItem onClick={handleDelete}>
                    {t('common:delete', 'Delete')}
                  </MenuItem>
                </>
              )}
            </Menu>
          </Box>
        </Box>
      </Box>
      <DragIcon {...listeners} className={classes.dragHandle} />
    </Box>
  );
};

export default Question;
